/**
 * Vendometro.tsx — Thermometer of Sales
 * Pure CSS/SVG component; no external visual lib required.
 * Color gradient: Red (0-33%) → Yellow (34-66%) → Green (67-99%) → Purple (100%+)
 */
import { useState } from 'react';
import { formatBRL, getThermometerColor } from '../../services/salesService';

interface VendometroProps {
  metaMensal: number;
  totalVendas: number;
  onMetaChange: (newMeta: number) => void;
  isUpdating?: boolean;
}

// ── View: thermometer SVG ──────────────────────────────────────────────────
const ThermometerSVG = ({ percentage, color }: { percentage: number; color: string }) => {
  const TUBE_HEIGHT = 180;
  const fillHeight  = Math.min((percentage / 100) * TUBE_HEIGHT, TUBE_HEIGHT);
  const fillY       = 20 + (TUBE_HEIGHT - fillHeight);

  return (
    <svg viewBox="0 0 60 240" className="w-14 h-56" aria-label="Termômetro de vendas">
      {/* Tube background */}
      <rect x="22" y="20" width="16" height={TUBE_HEIGHT} rx="8" fill="#e5e7eb" />

      {/* Fill */}
      <rect
        x="22"
        y={fillY}
        width="16"
        height={fillHeight}
        rx="8"
        fill={color}
        style={{ transition: 'all 0.8s cubic-bezier(0.4,0,0.2,1)' }}
      />

      {/* Bulb */}
      <circle cx="30" cy="220" r="16" fill={color} style={{ transition: 'fill 0.8s ease' }} />
      <circle cx="30" cy="220" r="10" fill="white" opacity="0.25" />

      {/* Tick marks */}
      {[0, 25, 50, 75, 100].map((pct) => {
        const y = 200 - (pct / 100) * TUBE_HEIGHT;
        return (
          <g key={pct}>
            <line x1="40" y1={y} x2="46" y2={y} stroke="#9ca3af" strokeWidth="1.5" />
            <text x="49" y={y + 4} fontSize="7" fill="#6b7280">{pct}%</text>
          </g>
        );
      })}
    </svg>
  );
};

// ── Logic + View: main component ───────────────────────────────────────────
export default function Vendometro({
  metaMensal,
  totalVendas,
  onMetaChange,
  isUpdating = false,
}: VendometroProps) {
  const [editMode, setEditMode]     = useState(false);
  const [inputValue, setInputValue] = useState('');

  const faltaVender  = Math.max(metaMensal - totalVendas, 0);
  const percentual   = metaMensal > 0 ? Math.min((totalVendas / metaMensal) * 100, 100) : 0;
  const color        = getThermometerColor(metaMensal > 0 ? (totalVendas / metaMensal) * 100 : 0);
  const metaAtingida = totalVendas >= metaMensal && metaMensal > 0;

  const handleSaveMeta = () => {
    const val = parseFloat(inputValue.replace(/[^0-9.,]/g, '').replace(',', '.'));
    if (!isNaN(val) && val >= 0) {
      onMetaChange(val);
      setEditMode(false);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-5 flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold text-gray-800 dark:text-white">
          🌡️ Vendômetro
        </h2>
        {metaAtingida && (
          <span className="text-xs font-bold text-purple-600 bg-purple-100 px-2 py-0.5 rounded-full animate-pulse">
            🎉 Meta Atingida!
          </span>
        )}
      </div>

      <div className="flex gap-6 items-end">
        {/* Thermometer SVG */}
        <ThermometerSVG percentage={percentual} color={color} />

        {/* Numbers */}
        <div className="flex-1 space-y-3">
          {/* realizado */}
          <div>
            <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Realizado</p>
            <p className="text-2xl font-bold" style={{ color }}>
              {formatBRL(totalVendas)}
            </p>
            <div className="mt-1 w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
              <div
                className="h-1.5 rounded-full transition-all duration-700"
                style={{ width: `${percentual}%`, backgroundColor: color }}
              />
            </div>
          </div>

          {/* meta */}
          <div>
            <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Meta do Mês</p>
            {editMode ? (
              <div className="flex gap-2 mt-1">
                <input
                  autoFocus
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSaveMeta()}
                  placeholder="ex: 5000"
                  className="w-28 border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm dark:bg-gray-700 dark:text-white"
                />
                <button
                  onClick={handleSaveMeta}
                  disabled={isUpdating}
                  className="text-xs bg-green-600 text-white px-2 py-1 rounded hover:bg-green-700 disabled:opacity-50"
                >
                  {isUpdating ? '…' : 'Salvar'}
                </button>
                <button
                  onClick={() => setEditMode(false)}
                  className="text-xs text-gray-500 hover:text-gray-700"
                >
                  ✕
                </button>
              </div>
            ) : (
              <div className="flex items-baseline gap-2 mt-1">
                <span className="text-lg font-semibold text-gray-700 dark:text-gray-200">
                  {formatBRL(metaMensal)}
                </span>
                <button
                  onClick={() => { setInputValue(String(metaMensal)); setEditMode(true); }}
                  className="text-xs text-blue-500 hover:text-blue-700 underline"
                >
                  editar
                </button>
              </div>
            )}
          </div>

          {/* falta vender */}
          <div>
            <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Falta Vender</p>
            <p className="text-lg font-semibold text-red-500 dark:text-red-400">
              {metaAtingida ? '—' : formatBRL(faltaVender)}
            </p>
          </div>

          {/* percentage badge */}
          <div
            className="inline-flex items-center gap-1 text-sm font-bold px-3 py-1 rounded-full"
            style={{ backgroundColor: `${color}20`, color }}
          >
            {percentual.toFixed(1)}% da meta
          </div>
        </div>
      </div>
    </div>
  );
}
